import {getPreferencesFromCookie, CONSENT_COOKIE_NAME} from '@github-ui/cookie-consent'
import {gpcDisabled} from '@github-ui/do-not-track'

const INSTRUMENTATION_KEY = 'b588e12fde784edfbd5ba42a17219be0-c9e70b20-6770-476b-8e75-5292a6fd04e2-7448'

const config = {
  instrumentationKey: INSTRUMENTATION_KEY,
  propertyConfiguration: {
    cookieDomain: 'github.com',
    userConsentCookieName: CONSENT_COOKIE_NAME,
    gpcDataSharingOptIn: gpcDisabled(),
    callback: {
      userConsentDetails: () => getPreferencesFromCookie(),
    },
  },
  webAnalyticsConfiguration: {
    urlCollectHash: true,
    urlCollectQuery: true,
  },
}

// Minimal typed surface of the Microsoft 1DS `ApplicationInsights` API we use. The SDK's
// bundled types expose the constructor only as a bare callable returning `any`, so we model
// the instance members we call and cast the constructor at the single import seam below.
type ApplicationInsightsInstance = {
  initialize(configuration: typeof config, plugins: unknown[]): void
  isInitialized(): boolean
  trackPageAction(event: PageActionEventType, properties: PageActionPropertiesType): void
}

// The ~600KB SDK is imported lazily inside `initializeAnalytics()` so it stays off the
// initial page-load parse path and only loads after the visitor grants analytics consent.
// For do-not-track / non-consenting visitors the consent element never calls
// `initializeAnalytics()`, so this promise simply stays pending and nothing is tracked. When
// initialization is attempted it resolves with the initialized instance, or null if the SDK
// fails to load or initialize, so callers await a properly-typed instance instead of
// guarding a mutable module-level reference.
const analyticsInstance = Promise.withResolvers<ApplicationInsightsInstance | null>()

export async function waitForInitialization(): Promise<boolean> {
  return (await analyticsInstance.promise) !== null
}

let initializePromise: Promise<void> | undefined

export function initializeAnalytics(): Promise<void> {
  initializePromise ??= (async () => {
    try {
      const {ApplicationInsights} = (await import('./ms.analytics-web')).default
      const ApplicationInsightsConstructor = ApplicationInsights as unknown as new () => ApplicationInsightsInstance
      const instance = new ApplicationInsightsConstructor()
      instance.initialize(config, [])
      analyticsInstance.resolve(instance.isInitialized() ? instance : null)
    } catch {
      // If the SDK fails to load or initialize, resolve to null so callers awaiting the
      // instance unblock and no-op instead of hanging.
      analyticsInstance.resolve(null)
    }
  })()
  return initializePromise
}

export type PageActionEventType = {
  behavior: PageActionBehavior
  name: string
  uri: string
  properties: {
    pageTags: PageActionEventTagsType
  }
}

export type PageActionEventTagsType = {
  gitHubAccountType?: string
  orderInfo?: PageActionOrderInfoType
  metaTags?: {
    [name: string]: unknown
  }
  [name: string]: unknown
}

export type PageActionOrderInfoType = {
  id: string
  lnItms: PageActionLineItemType[]
}

export type PageActionLineItemType = {
  title: string
  qty?: number
  prdType: PageActionPeriodType
}

export const PageActionBehavior = {
  buyIntent: 20,
  purchase: 87,
  trial: 200,
  contact: 162,
} as const

export type PageActionBehavior = (typeof PageActionBehavior)[keyof typeof PageActionBehavior]

export const PageActionAccountType = {
  New: 'new',
  Existing: 'existing',
} as const

export type PageActionAccountType = (typeof PageActionAccountType)[keyof typeof PageActionAccountType]

export const PageActionPeriodType = {
  Month: 'month',
  Year: 'year',
  Free: 'free',
} as const

export type PageActionPeriodType = (typeof PageActionPeriodType)[keyof typeof PageActionPeriodType]

export type PageActionPropertiesType = {
  refUri: string
}

export async function trackPageAction(
  pageActionEvent: PageActionEventType,
  pageActionProperties: PageActionPropertiesType,
) {
  const instance = await analyticsInstance.promise
  if (instance) {
    instance.trackPageAction(pageActionEvent, pageActionProperties)
  }
}
